import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import {
  useEditionIntegrityCheck,
  useTradeIdeaEditionArchive,
  useTradeIdeaEditionByAsOf,
  useTradeIdeaEditionToday,
} from "@/domains/trade-ideas/hooks/useTradeIdeas";
import type {
  EditionArchiveItem,
  EditionCandidateFunnel,
  EditionDetail,
  EnergyScoreSnapshot,
  MultiStrategyBestIdea,
  MultiStrategyScreenEntry,
  MultiStrategyScreenRow,
} from "@/domains/trade-ideas/lib/contracts";
import {
  buildDeskIdeaWatchInitialState,
  DIRECTION_UNAVAILABLE_REASON,
  deskIdeaWatchAvailability,
  hasKnownDirection,
  useDeskIdeaStalenessInvalidation,
} from "@/domains/trade-ideas/lib/deskIdeaWatchPrefill";
import { LIQUIDITY_TONE_CLASS, VOL_REGIME_TONE_CLASS } from "@/domains/trade-ideas/lib/deskThresholdPresentation";
import { buildTickerContextLink } from "@/domains/trade-ideas/lib/tickerContextLink";
import { AskClaudeButton } from "@/domains/trade-ideas/components/AskClaudeButton";
import { IdeaDeepDiveSections } from "@/domains/trade-ideas/components/ThesisQuestionSections";
import {
  archetypeLabel,
  deriveWatchOutCodes,
  directionView,
  displayEntryResolution,
  editionRegimeLine,
  energyRatingValue,
  energySignalCoverageLabel,
  energySignalLabel,
  exitRuleCopy,
  formatEvPerShare,
  formatImpliedProbabilityPct,
  formatSpreadPct,
  IMPLIED_PROBABILITY_TOOLTIP,
  midVsFillLabel,
  reasonCodeLabel,
  regimeCopy,
  runningRecordFacts,
  spreadLiquiditySignifier,
  volRegimeTone,
} from "@/domains/trade-ideas/lib/screenerLabels";
import { isApiError } from "@/shared/lib/apiClient";
import { DeskRulesStrip } from "@/domains/trade-ideas/components/DeskRulesStrip";
import type { ApiFn } from "@/shared/lib/types";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { cn } from "@/shared/lib/utils";

// ---------------------------------------------------------------------------
// The daily edition: the platform's dated, published, write-once desk sheet
// (JS-1059's backend + JS-1060's frontend). INTERNAL-ONLY -- gated behind the
// same TRADE_IDEAS_INTERNAL_USER_IDS allowlist as the /screener read path
// (see services/apis/domains/trade_ideas/edition.py's module docstring);
// a 403 here means "the caller isn't internal", not a bug.
//
// Information hierarchy IS the deliverable (per the product owner's parked
// visual design): masthead -> regime line -> ideas -> book -> scoreboard ->
// archive. Existing design-system primitives only, no bespoke visual work.
// ---------------------------------------------------------------------------

function parseFit(value: string | null | undefined): number {
  if (value == null) return NaN;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function topEntry(row: MultiStrategyScreenRow): MultiStrategyScreenEntry | undefined {
  return [...row.entries].sort((a, b) => {
    const fa = parseFit(a.fit_score);
    const fb = parseFit(b.fit_score);
    if (Number.isNaN(fa) && Number.isNaN(fb)) return 0;
    if (Number.isNaN(fa)) return 1;
    if (Number.isNaN(fb)) return -1;
    return fb - fa;
  })[0];
}

function isShadowEntry(entry: MultiStrategyScreenEntry | undefined): boolean {
  if (!entry) return false;
  if (entry.shadow === true) return true;
  if (entry.customer_eligible === false) return true;
  return entry.authority_label === "SHADOW_RESEARCH";
}

function bestIdea(row: MultiStrategyScreenRow): MultiStrategyBestIdea | null {
  return (row.best_idea ?? null) as MultiStrategyBestIdea | null;
}

function signalScoreValue(row: MultiStrategyScreenRow): number | null {
  const raw = row.best_idea_score ?? bestIdea(row)?.quality_score;
  if (raw == null || raw === "") return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(Math.max(0, Math.min(1, parsed)) * 100);
}

function formatEditionDate(asOf: string | null | undefined): string {
  if (!asOf) return "Unknown date";
  // as_of is a plain ET market date (YYYY-MM-DD); parse as UTC-noon so no
  // local-timezone rollback shifts the displayed calendar day.
  const parsed = new Date(`${asOf}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return asOf;
  return parsed.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

// ---------------------------------------------------------------------------
// Masthead
// ---------------------------------------------------------------------------

// JS-1089 (edition parity #3): the edition's OWN publication instant --
// `generated_at` is the wall-clock stamp of when THIS edition was assembled
// (edition.py's `DailyEdition.generated_at`), distinct from any individual
// idea's own `generated_at`. Rendered in the reader's local time, matching
// the house `toLocaleString()` convention used for every other publication/
// evidence timestamp in this domain (see SetupDetailDialog.tsx).
function formatPublicationTime(generatedAt: string | null | undefined): string | null {
  if (!generatedAt) return null;
  const parsed = new Date(generatedAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString();
}

// JS-1090b: "Verify integrity" -- an on-demand check of this edition's
// content hash + prior-edition linkage (GET
// /api/v1/trade-ideas/editions/{as_of}/integrity), plus a purely
// CLIENT-SIDE "Download canonical payload" JSON blob save (no network
// upload). Additive and self-contained: a sibling of EditionMasthead's
// existing Provenance Tooltip, not a restructure of it.
function EditionIntegrityAffordance({ asOf, api }: { asOf: string | null; api: ApiFn }) {
  const integrityCheck = useEditionIntegrityCheck(api);

  if (!asOf) return null;

  const handleVerify = () => {
    integrityCheck.mutate(asOf);
  };

  const handleDownload = () => {
    const payload = integrityCheck.data?.canonical_payload;
    // Round 2 (adversarial review, MED FIX 7 -- null download): the schema
    // types `canonical_payload` as `z.unknown()`, and the backend's own
    // `get_integrity_trail` genuinely returns `None` there for a document
    // this verification path could never canonicalize (a LEGACY_UNVERIFIABLE
    // candidate, or one that failed canonicalization outright) -- `null` is
    // a REAL, distinct outcome from `undefined` (not-yet-fetched), and the
    // original `payload === undefined` guard let a click on a null payload
    // silently no-op with no feedback. Guarded by the render condition below
    // instead: the button itself is never shown when there is nothing to
    // download, so this handler is only reachable with a real payload.
    if (payload == null) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `edition-${asOf}-canonical-payload.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const result = integrityCheck.data;
  const statusLabel =
    result?.hash_verification_status === "VERIFIED"
      ? "Verified"
      : result?.hash_verification_status === "MISMATCH"
        ? "Mismatch"
        : result?.hash_verification_status === "LEGACY_UNVERIFIABLE"
          ? "Legacy — unverifiable"
          : null;
  const statusToneClass =
    result?.hash_verification_status === "VERIFIED"
      ? "text-gain"
      : result?.hash_verification_status === "MISMATCH"
        ? "text-loss"
        : "text-warning";
  // Round 2 FIX 7: download only when there is a REAL payload to reconstruct
  // -- `canonical_payload` arrives `null` for a candidate this verification
  // path could never canonicalize (typically alongside
  // LEGACY_UNVERIFIABLE, but checked directly rather than inferred from
  // status, since the field itself is the authoritative signal).
  const hasCanonicalPayload = result != null && result.canonical_payload != null;

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleVerify}
        disabled={integrityCheck.isPending}
        title={integrityCheck.isPending ? "Verifying…" : undefined}
        className="cursor-pointer underline decoration-dotted underline-offset-2 disabled:cursor-wait disabled:opacity-60"
      >
        {integrityCheck.isPending ? "Verifying…" : "Verify integrity"}
      </button>
      {integrityCheck.isError && <span className="text-loss">Could not verify.</span>}
      {statusLabel && (
        <span className={cn("font-medium", statusToneClass)} role="status">
          {statusLabel}
          {result?.hash_contract_era ? ` (${result.hash_contract_era})` : ""}
          {result?.hash_verification_status === "MISMATCH" && result.hash_verification_failure_reason
            ? `: ${result.hash_verification_failure_reason}`
            : ""}
        </span>
      )}
      {result &&
        (hasCanonicalPayload ? (
          <button
            type="button"
            onClick={handleDownload}
            className="cursor-pointer underline decoration-dotted underline-offset-2"
          >
            Download canonical payload
          </button>
        ) : (
          // FIX 7: honest, never a silently-inert download button.
          <span className="text-muted-foreground">No canonical payload can be reconstructed.</span>
        ))}
    </span>
  );
}

function EditionMasthead({ edition, api }: { edition: EditionDetail; api: ApiFn }) {
  const publishedAt = formatPublicationTime(edition.generated_at);
  // JS-1089 (STOP TRUNCATING #3): the content_hash / edition_id identifiers
  // move OFF the running masthead copy and into a small labeled "Provenance"
  // affordance (copy-voice rule: identifiers labeled as provenance, never
  // woven into prose) -- publication time is now the visible fact instead.
  const provenanceItems = [
    edition.edition_id ? { label: "Edition ID", value: edition.edition_id } : null,
    edition.content_hash ? { label: "Content hash", value: edition.content_hash } : null,
  ].filter((item): item is { label: string; value: string } => item != null);
  return (
    <header className="border-b border-border pb-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {edition.edition_number != null ? `Edition #${edition.edition_number}` : "Edition"}
          <span className="mx-2 text-muted-foreground" aria-hidden>
            ·
          </span>
          <span className="text-foreground">{formatEditionDate(edition.as_of)}</span>
        </h1>
        <Badge variant="secondary" className="border-info/25 bg-info/10 text-info">
          Internal · Shadow research
        </Badge>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {publishedAt && <span>Published {publishedAt}</span>}
        {provenanceItems.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                role="note"
                className="cursor-help underline decoration-dotted underline-offset-2"
              >
                Provenance
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[360px]">
              <dl className="space-y-1">
                {provenanceItems.map((item) => (
                  <div key={item.label} className="flex items-baseline justify-between gap-3">
                    <dt className="text-muted-foreground">{item.label}</dt>
                    <dd className="break-all font-mono text-[10px]">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </TooltipContent>
          </Tooltip>
        )}
        <EditionIntegrityAffordance asOf={edition.as_of ?? null} api={api} />
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Regime line
// ---------------------------------------------------------------------------

function RegimeLine({ edition }: { edition: EditionDetail }) {
  const line = editionRegimeLine(edition.regime_summary);
  return (
    <p className="text-sm text-foreground">
      <span className="font-medium">{line}</span>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Ideas as cards -- reuses the same rendering vocabulary as
// MultiStrategyScreenTable's ThesisDrawer (economics, mid·fill, spread-%,
// Implied PoP with the same tooltip constant, exit rule via reason codes,
// shadow band distinction, score + axes) but in a card layout rather than a
// row/drawer, since an edition reads as an archived one-shot sheet, not a
// live sortable table.
// ---------------------------------------------------------------------------

function IdeaCardEconomics({ entry }: { entry: MultiStrategyScreenEntry }) {
  const hasEconomics =
    entry.maximum_loss_usd != null || entry.maximum_profit_usd != null || entry.natural_entry_per_share != null;
  if (!hasEconomics) {
    // JS-1071: honest, code-driven headline instead of the hardcoded
    // "needs market-hours quotes" claim -- the actual abstain code
    // (DEBIT_ECONOMICS_NO_ELIGIBLE_EXPRESSION in the common case) is
    // structural, not a market-hours/quote-freshness issue.
    const abstain = entry.abstain_reason_codes ?? [];
    const headline =
      abstain.length > 0 ? reasonCodeLabel(abstain[0]) : reasonCodeLabel("DEBIT_ECONOMICS_NO_ELIGIBLE_EXPRESSION");
    return (
      <div className="space-y-1">
        <p className="text-[12px] text-muted-foreground">{headline}</p>
        {abstain.length > 0 && (
          <ul className="space-y-0.5">
            {abstain.map((code) => (
              <li key={code} className="text-[11px] text-muted-foreground">· {reasonCodeLabel(code)}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  const friction = entry.execution_friction;
  const probability = entry.implied_probability;
  const fillLabel = friction ? midVsFillLabel(friction.net_mid_per_share, friction.net_realistic_fill_per_share) : null;
  const spreadLabel = friction ? formatSpreadPct(friction.spread_width_pct_of_premium) : null;
  // JS-1086 (hi-fi Desk): the SAME threshold treatment
  // MultiStrategyScreenTable's ExecutionFrictionRows already applies to this
  // fact -- amber/red coloring ALWAYS paired with the "wide"/"very wide"
  // text signifier (never color-only), via the shared LIQUIDITY_TONE_CLASS
  // map so the two idea surfaces never diverge on this mapping.
  const spreadSignifier = friction ? spreadLiquiditySignifier(friction.spread_width_pct_of_premium) : null;
  const popLabel = probability ? formatImpliedProbabilityPct(probability.probability_of_profit) : null;
  const evLabel = probability ? formatEvPerShare(probability.expected_value_per_share) : null;

  return (
    <dl className="mt-2 space-y-1">
      {entry.maximum_loss_usd != null && (
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Max loss</dt>
          <dd className="font-mono text-[12px] tabular-nums text-foreground">${entry.maximum_loss_usd}</dd>
        </div>
      )}
      {entry.maximum_profit_usd != null && (
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Max profit</dt>
          <dd className="font-mono text-[12px] tabular-nums text-foreground">${entry.maximum_profit_usd}</dd>
        </div>
      )}
      {entry.strikes.length > 0 && (
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Strikes</dt>
          <dd className="font-mono text-[12px] tabular-nums text-foreground">{entry.strikes.join(" / ")}</dd>
        </div>
      )}
      {entry.expiry && (
        <div className="flex items-baseline justify-between gap-3">
          {/* JS-1087 (fix 4): this row only ever rendered `entry.expiry` (a
              bare date) under a "Exit / expiry" label -- reading as though
              an exit rule were also shown here when none is. Label it
              honestly as "Expiry" unless the entry's own stamped exit rule
              (exitRuleCopy, the same function ExitRuleNote uses) actually
              resolves, in which case the combined label is truthful. */}
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {exitRuleCopy(entry.exit_rule) != null ? "Exit / expiry" : "Expiry"}
          </dt>
          <dd className="font-mono text-[12px] tabular-nums text-foreground">{entry.expiry}</dd>
        </div>
      )}
      {fillLabel != null && (
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Entry price</dt>
          <dd className="font-mono text-[12px] tabular-nums text-foreground">{fillLabel}</dd>
        </div>
      )}
      {spreadLabel != null && (
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Spread / premium</dt>
          <dd
            className={cn(
              "font-mono text-[12px] tabular-nums",
              LIQUIDITY_TONE_CLASS[spreadSignifier?.tone ?? "neutral"],
            )}
            aria-label={spreadSignifier?.label ? `${spreadLabel}, ${spreadSignifier.label}` : spreadLabel}
          >
            {spreadLabel}
            {spreadSignifier?.label && (
              <span className="ml-1 font-sans text-[10px] normal-case text-inherit">{spreadSignifier.label}</span>
            )}
          </dd>
        </div>
      )}
      {(popLabel != null || evLabel != null) && (
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  role="note"
                  aria-label={`Implied PoP: ${IMPLIED_PROBABILITY_TOOLTIP}`}
                  className="cursor-help underline decoration-dotted underline-offset-2"
                >
                  Implied PoP
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[260px]">{IMPLIED_PROBABILITY_TOOLTIP}</TooltipContent>
            </Tooltip>
          </dt>
          <dd className="font-mono text-[12px] tabular-nums text-foreground">
            {popLabel ?? "—"}
            {evLabel != null && <span className="ml-1.5 text-muted-foreground">EV {evLabel}/sh</span>}
          </dd>
        </div>
      )}
    </dl>
  );
}

// JS-1066: the same Energy badge the screener table renders, for idea cards
// (CORE rows get no badge -- absence = default). Duplicated presentational
// atom rather than a cross-file import, matching how this page already
// re-implements the screener's other row-level atoms (topEntry,
// isShadowEntry, signalScoreValue) as its own local card-layout copies.
function EnergyBadge({ snapshot }: { snapshot: EnergyScoreSnapshot | null | undefined }) {
  if (!snapshot) return null;
  const composite = energyRatingValue(snapshot.composite);
  const coverage = energySignalCoverageLabel(snapshot.signals_available);
  const contributing = Object.entries(snapshot.signals ?? {})
    .map(([name, result]) => ({ name, value: energyRatingValue(result?.score) }))
    .filter((entry): entry is { name: string; value: number } => entry.value !== null);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="cursor-default border-warning/30 bg-warning/10 text-warning"
          aria-label={`Energy rating: ${composite ?? "unavailable"}`}
        >
          Energy {composite ?? "—"}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-[240px]">
        <div className="space-y-1">
          {contributing.length > 0 ? (
            <ul className="space-y-0.5">
              {contributing.map((entry) => (
                <li key={entry.name} className="flex items-center justify-between gap-3">
                  <span>{energySignalLabel(entry.name)}</span>
                  <span className="font-mono tabular-nums">{entry.value}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>No contributing sub-signals available.</p>
          )}
          {coverage && <p className="text-muted-foreground">{coverage}</p>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function IdeaCard({ row, editionAsOf }: { row: MultiStrategyScreenRow; editionAsOf: string | null }) {
  const navigate = useNavigate();
  const top = topEntry(row);
  const shadow = isShadowEntry(top);
  // JS-1087 (fix 1): `row.direction` is `z.string().nullish()` on the wire
  // (contracts.ts) -- an old `=== "BULLISH" ? bullish : bearish` two-way
  // branch renders a null/unrecognized direction as a confident, colored
  // "BEARISH" badge. `directionView` is the SAME three-way classifier
  // MultiStrategyScreenTable's row uses (NO-TECH-DEBT).
  const direction = directionView(row.direction);
  const signal = signalScoreValue(row);

  // JS-1091 round 3 (FIX 2): the edition's IdeaCard now owns Q1 (setup
  // thesis) EXACTLY like the live screener drawer -- see
  // MultiStrategyScreenTable.tsx's ThesisDrawer, the reference
  // implementation this mirrors field-for-field (NO-TECH-DEBT: same
  // decision, same source fields, not a parallel re-derivation). BOUND
  // (a real, current SetupSnapshot) renders the projected setup thesis
  // (why_now facts + activation/invalidation/horizon); UNBOUND or a legacy
  // row (no setup_binding at all) falls back to the entry-derived reason
  // codes, explicitly labeled as screen-level evidence, never presented as
  // a setup thesis.
  const isSetupBound = row.setup_binding?.status === "BOUND" && row.setup != null;
  const setup = isSetupBound ? row.setup : undefined;
  // JS-1089 (edition parity): the product review flagged reasons truncated
  // to three as one of the edition's lossy behaviors -- the FULL published
  // rationale (every reason code, humanized) renders here now, matching
  // MultiStrategyScreenTable's own drawer, which has never truncated this
  // list (see its "Why now" QuestionSection).
  const whyCodes = setup ? [] : (top?.reason_codes ?? []);

  // JS-1071: `top` (highest-fit) still owns the card's row identity (shadow
  // badge, direction, "why this"), but the structure label and economics
  // panel show the best-PRICED entry -- under the failsafe fit ranking
  // (active while iv_rank is None) `top` is almost always the unpriced
  // vertical, which hid a sibling entry (e.g. a priced long call) behind a
  // false "pending" message.
  //
  // JS-1091 round 3 (FIX 1 + FIX 2): a present-but-unmatched
  // primary_expression_id stamp is a genuine data inconsistency (see
  // screenerLabels.displayEntryResolution) -- an edition card must render
  // that explicitly too, never silently substitute `top` as if it were the
  // resolved display entry.
  const displayResolution = displayEntryResolution(row);
  const isInconsistent = displayResolution.status === "inconsistent";
  const display = displayResolution.status === "ok" ? displayResolution.entry : undefined;
  const displayIsAlternate = Boolean(display && top && display !== top);
  // JS-1091 round 3 (FIX 2): axes/confidence/Strategy-match are claims
  // about `display` (the SELECTED expression), sourced from ITS OWN
  // `expression_best_idea` breakdown -- never the row-level `best_idea`,
  // which is keyed to the PRIMARY expression and would misattribute the
  // breakdown for a non-primary alternative. OMITTED entirely (never
  // substituted with the row-level score) when absent -- a legacy row with
  // no per-expression score at all.
  const idea = display ? (display.expression_best_idea as MultiStrategyBestIdea | null) : null;

  // JS-1089 (edition parity): the expandable four-question detail -- the
  // SAME argument (Q1 why now / Q2 what has to happen / Q3 what if wrong /
  // Q4 how do I leave) MultiStrategyScreenTable's ThesisDrawer renders,
  // reusing the SAME extracted section components (NO-TECH-DEBT). Collapsed
  // by default so the edition list stays scannable -- the product review's
  // complaint was that the edition DROPPED this evidence, not that every
  // card must show it inline; expanding one reveals the full argument.
  const [expanded, setExpanded] = useState(false);
  const abstain = display?.abstain_reason_codes ?? [];
  const abstainHeadline = abstain.length > 0 ? reasonCodeLabel(abstain[0]) : reasonCodeLabel("DEBIT_ECONOMICS_NO_ELIGIBLE_EXPRESSION");
  // JS-1089 round 2 (FIX 2): the SAME row-fact-only derivation
  // MultiStrategyScreenTable's ThesisDrawer calls -- sourced only from THIS
  // row's own frozen `relative_strength`/`options_flow`/`earnings` facts, so
  // an archived edition's watch-outs are never silently absent.
  // DESK-009 (JS-1144): the SELECTED expression's own row-face disclosures
  // ride along, so an archived edition shows the same caveats the live Desk
  // does -- the parity this call site already exists to preserve.
  const watchOutCodes = deriveWatchOutCodes(row, display);

  // JS-1089 (edition parity, STOP TRUNCATING #3): policy/identity
  // identifiers -- NEVER woven into running prose (copy-voice rule) -- go
  // into a small labeled "Provenance" affordance instead. `generated_at` is
  // this ROW's own wall-clock publication instant (non-canonical passthrough
  // field, same accessor MultiStrategyScreenTable.tsx's rowGeneratedAt uses).
  const rowGeneratedAtRaw = (row as Record<string, unknown>).generated_at;
  const rowGeneratedAt = typeof rowGeneratedAtRaw === "string" ? rowGeneratedAtRaw : null;
  const provenanceItems: { label: string; value: string }[] = [];
  if (row.row_id) provenanceItems.push({ label: "Row", value: row.row_id });
  if (display?.expression_ref) provenanceItems.push({ label: "Expression", value: display.expression_ref });
  if (idea?.policy_version) provenanceItems.push({ label: "Policy", value: idea.policy_version });
  if (idea?.policy_hash) provenanceItems.push({ label: "Policy hash", value: idea.policy_hash });
  if (rowGeneratedAt) {
    const parsed = new Date(rowGeneratedAt);
    provenanceItems.push({
      label: "Published",
      value: Number.isNaN(parsed.getTime()) ? rowGeneratedAt : parsed.toLocaleString(),
    });
  }

  // JS-1081: "+Watch" pre-fill -- same shared honest-availability check and
  // WatchWizardInitialState builder as MultiStrategyScreenTable's thesis
  // drawer (NO-TECH-DEBT: one shared utility, not duplicated logic).
  //
  // JS-1087 (fix 1): a "+Watch" pre-fill encodes a directional position, so
  // it must fail closed when the row's own direction is unrecognized --
  // checked FIRST, same ordering as MultiStrategyScreenTable's drawer.
  //
  // JS-1098 (Codex round, HIGH): `deskIdeaWatchAvailability` now also takes
  // this row's own `generated_at` (`rowGeneratedAt`, already derived above
  // for the Provenance affordance) so a "stale · re-check" row fails closed
  // on +Watch too -- same shared gate MultiStrategyScreenTable's drawer
  // should call identically (see that file's required diff, out of scope
  // here per this change's constraints).
  const knownDirection = hasKnownDirection(row);
  const watchAvailability = !knownDirection
    ? { available: false as const, reason: DIRECTION_UNAVAILABLE_REASON }
    : display
      ? deskIdeaWatchAvailability(display, rowGeneratedAt)
      : { available: false as const, reason: "This idea has not been priced yet." };

  // JS-1098 (Codex round, HIGH): if this card sits open past the moment its
  // OWN row crosses into the "stale · re-check" tier, re-invalidate the
  // edition query ONCE at that boundary (not a polling loop) so
  // `watchAvailability` above recomputes correctly disabled without
  // requiring a manual refresh. Scoped to the shared ["trade-ideas",
  // "edition"] key prefix so it invalidates whichever of
  // useTradeIdeaEditionToday/useTradeIdeaEditionByAsOf is actually active.
  const queryClient = useQueryClient();
  useDeskIdeaStalenessInvalidation(rowGeneratedAt, () => queryClient.invalidateQueries({ queryKey: ["trade-ideas", "edition"] }));
  const openPrefilledWatch = () => {
    if (!knownDirection || !display) return;
    const initialState = buildDeskIdeaWatchInitialState(row, display);
    if (!initialState) {
      // JS-1094 (demo blocker FIX 3): same defense-in-depth honest failure
      // as MultiStrategyScreenTable's identical click handler -- the button
      // is only enabled when `watchAvailability.available`, but a race or
      // decode-drift edge case could still make the builder decline here.
      // Never a silent no-op.
      toast.error("This idea can't be pre-filled into a watch right now — try refreshing.");
      return;
    }
    navigate("/portal/watches/new", { state: { watchWizardInitialState: initialState } });
  };

  // JS-1082: "Open <ticker> ticker view" -- same shareable deep-link builder
  // MultiStrategyScreenTable's thesis drawer uses (NO-TECH-DEBT: one shared
  // helper). `null` (never a guessed link) when the display entry has no
  // confirmed `ledger_id`.
  //
  // JS-1087 (adversarial-review fix): also gated on `knownDirection` -- see
  // MultiStrategyScreenTable.tsx's identical fix for the full rationale (an
  // unknown-direction idea previously escaped the fail-closed gate applied
  // to "+Watch"/"Ask Claude" via this link, and the ticker dashboard maps a
  // non-BULLISH/BEARISH direction to an invisible empty-string state).
  const tickerContextHref = display?.ledger_id && knownDirection
    ? buildTickerContextLink({
        ticker: row.ticker ?? "",
        ledgerId: display.ledger_id,
        origin: "edition",
        rowId: row.row_id,
        editionAsOf,
      })
    : null;

  // JS-1082: "back to idea" (tickerContextLink.ts's buildBackToIdeaHref)
  // arrives here as `?openRowId=<row_id>` -- scroll this card into view once
  // it matches, so the visitor lands back on exactly the idea they left.
  const [searchParams] = useSearchParams();
  const requestedOpenRowId = searchParams.get("openRowId");
  const cardRef = useRef<HTMLElement | null>(null);
  const isRequestedOpenRow = Boolean(row.row_id) && row.row_id === requestedOpenRowId;
  useEffect(() => {
    if (!isRequestedOpenRow) return;
    // JS-1086 (hi-fi Desk) a11y: respect prefers-reduced-motion -- an
    // instant jump instead of a smooth animated scroll for a reader who has
    // asked their OS/browser to minimize motion.
    const reduceMotion =
      typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    cardRef.current?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
  }, [isRequestedOpenRow]);

  return (
    <article
      ref={cardRef}
      className={cn(
        "min-w-0 rounded-lg border bg-card p-4",
        shadow ? "border-info/30 bg-info/5" : "border-border",
        isRequestedOpenRow && "ring-2 ring-primary",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-stretch gap-2.5">
          {/* JS-1086 (hi-fi Desk): the SAME direction-rail idiom
              MultiStrategyScreenTable's ticket cell applies (a thin gain/loss
              accent bar), so a bullish/bearish idea reads identically on both
              the row and the card surface. JS-1087 (fix 1): a null/
              unrecognized direction gets the SAME neutral rail
              (bg-muted-foreground/50) MultiStrategyScreenTable's row uses,
              never a guessed gain/loss color. */}
          <span
            aria-hidden
            className={cn(
              "w-1 shrink-0 self-stretch rounded-full",
              direction.kind === "BULLISH" ? "bg-gain" : direction.kind === "BEARISH" ? "bg-loss" : "bg-muted-foreground/50",
            )}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-foreground">{row.ticker ?? "—"}</span>
              <span
                className={cn(
                  "inline-flex items-center gap-0.5 text-[11px] font-medium",
                  direction.kind === "BULLISH" ? "text-gain" : direction.kind === "BEARISH" ? "text-loss" : "text-muted-foreground",
                )}
              >
                {direction.kind === "BULLISH" ? (
                  <TrendingUp className="size-3" aria-hidden />
                ) : direction.kind === "BEARISH" ? (
                  <TrendingDown className="size-3" aria-hidden />
                ) : (
                  <Minus className="size-3" aria-hidden />
                )}
                {direction.label}
              </span>
              {shadow && (
                <Badge variant="outline" className="border-info/30 text-info">
                  shadow
                </Badge>
              )}
              {row.discovery_lane === "ENERGY" && (
                <EnergyBadge snapshot={row.energy_score_snapshot} />
              )}
              {/* JS-1091 round 3 (FIX 1): the SAME explicit "expression data
                  inconsistent" signal MultiStrategyScreenTable's row renders
                  -- see that file's InconsistentExpressionBadge -- for a
                  present-but-unmatched primary_expression_id stamp, never
                  silently substituted with a guessed entry. */}
              {isInconsistent && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="cursor-default border-warning/30 bg-warning/10 text-warning"
                      aria-label="Expression data inconsistent — re-run pending"
                    >
                      Data inconsistent
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    This idea&apos;s selected expression could not be resolved from its own data — expression
                    data inconsistent, re-run pending.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{archetypeLabel(display?.archetype)}</p>
            {/* JS-1086 (hi-fi Desk): the SAME vol-read tone treatment
                MultiStrategyScreenTable's row applies (cheap=gain, rich=warning,
                balanced=default, unknown=muted italic), via the shared
                VOL_REGIME_TONE_CLASS map -- color always paired with the
                regime's own text label. */}
            <p className={cn("mt-0.5 text-[11px]", VOL_REGIME_TONE_CLASS[volRegimeTone(row.iv_rv_regime)])}>
              {regimeCopy(row.iv_rv_regime).label}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-lg font-semibold tabular-nums text-foreground">
            {signal != null ? signal : "—"}
          </div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Signal</p>
        </div>
      </div>

      {display && (
        <>
          {displayIsAlternate && (
            <p className="mt-2 text-[11px] italic text-muted-foreground">
              Priced expression: {archetypeLabel(display.archetype)}
            </p>
          )}
          <IdeaCardEconomics entry={display} />
        </>
      )}

      {/* JS-1089 (edition parity): "if the edition is the record, it cannot
          be the lossy surface" -- the full four-question argument (exit
          rule, scenario grid, carry, event-vol, HAR-RV, OI context,
          breakevens, alternatives) lives here, collapsed by default so the
          edition list stays scannable. Reuses the SAME QuestionSection
          building blocks the screener drawer renders (NO-TECH-DEBT). */}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="mt-3 w-full justify-between px-2 text-[11px] text-muted-foreground hover:text-foreground"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded ? "Hide full argument" : "Show full argument"}
        <ChevronDown className={cn("size-3.5 transition-transform", expanded && "rotate-180")} aria-hidden />
      </Button>

      {expanded && (
        <div className="mt-2 space-y-4 rounded-md border border-border bg-surface/50 p-3">
          {display && (
            <IdeaDeepDiveSections
              entry={display}
              row={row}
              reasonCodes={whyCodes}
              setup={setup}
              watchOutCodes={watchOutCodes}
              abstainHeadline={abstainHeadline}
              idea={idea}
              includeBeAdr
            />
          )}
          {!display && (
            <p className="font-mono text-[11px] italic text-muted-foreground">
              No priced expression exists yet.
            </p>
          )}
          {/* JS-1089 (STOP TRUNCATING #3): policy/identity identifiers as a
              distinct, labeled provenance affordance -- NEVER woven into the
              running copy above (copy-voice rule: no version stamps/raw
              identifiers in prose). */}
          {provenanceItems.length > 0 && (
            <div className="border-t border-border pt-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    role="note"
                    className="cursor-help text-[10px] uppercase tracking-wide text-muted-foreground underline decoration-dotted underline-offset-2"
                  >
                    Provenance
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[320px]">
                  <dl className="space-y-1">
                    {provenanceItems.map((item) => (
                      <div key={item.label} className="flex items-baseline justify-between gap-3">
                        <dt className="text-muted-foreground">{item.label}</dt>
                        <dd className="break-all font-mono text-[10px]">{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      )}

      <Button
        size="sm"
        variant="outline"
        className="mt-3 w-full"
        disabled={!watchAvailability.available}
        title={watchAvailability.reason ?? undefined}
        onClick={openPrefilledWatch}
      >
        + Watch
      </Button>

      {/* JS-1082: disabled honestly (never hidden) when this idea has no
          confirmed ledger_id yet, OR (JS-1087 adversarial-review fix) when
          the row's own direction is unrecognized -- checked first, same
          ordering as "+Watch"/"Ask Claude". */}
      <Button
        asChild={Boolean(tickerContextHref)}
        size="sm"
        variant="outline"
        className="mt-2 w-full"
        disabled={!tickerContextHref}
        title={
          tickerContextHref
            ? undefined
            : !knownDirection
              ? DIRECTION_UNAVAILABLE_REASON
              : "This idea has no ledger reference yet."
        }
      >
        {tickerContextHref ? (
          <Link to={tickerContextHref}>Open {row.ticker ?? "ticker"} ticker view</Link>
        ) : (
          <span>Open ticker view</span>
        )}
      </Button>

      {/* JS-1083: "Ask Claude" -- same shared copy-to-clipboard prompt builder
          MultiStrategyScreenTable's thesis drawer uses (NO-TECH-DEBT).
          Disabled honestly (never hidden), matching the ticker-view button
          above, when this card has no priced display entry at all yet.
          JS-1091 round 2 (FIX 5): the prompt's signal-score fact reads
          `entry.expression_best_idea` (this SAME display entry's own
          score) -- `row` is no longer part of `promptInput` at all; see
          askClaudePrompt.ts's `signalScoreLabel`. */}
      <AskClaudeButton
        ledgerId={display?.ledger_id}
        promptInput={{
          ticker: row.ticker ?? "",
          entry: display ?? { archetype: null, entry_type: null, natural_entry_per_share: null, strikes: [], expiry: null, expression_best_idea: null },
          direction: row.direction,
        }}
        disabledReason={knownDirection ? null : DIRECTION_UNAVAILABLE_REASON}
        className="mt-2 w-full"
      />
    </article>
  );
}

// SHOULD-FIX 3: `droppedCount` is the number of raw idea entries the
// contract's per-row-tolerant decode (editionIdeasWithDropsSchema) could not
// parse -- surfaced as a small honest note, never a silent drop and never a
// full-page error over one bad snapshot.
// DESK-013 (JS-1150), ruling 3: the edition explains itself.
//
// Edition #3 (Sep 16) said "No ideas cleared the quality bar for this
// edition." and stopped there. That left the owner unable to tell a real
// quiet day from a broken pipeline -- which is what it actually was. This
// panel renders the funnel the edition now persists, so the empty state
// carries its own account: how many names the desk looked at, what held each
// one, and which bars were applied.
//
// Copy is deliberately desk vernacular ("what the desk looked at", "held",
// "quotes as of"), never the implementation's own vocabulary -- no reason
// codes are shown raw where a trader-readable phrase exists for them.
function CandidateFunnelPanel({ funnel }: { funnel: EditionCandidateFunnel }) {
  const held = funnel.per_ticker.filter((row) => row.outcome === "HELD");
  const rowsRead = funnel.rows_read ?? funnel.candidates_considered ?? 0;

  return (
    <div className="mt-3 rounded-lg border border-border bg-surface/40 px-4 py-3 text-left">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        What the desk looked at
      </h3>
      <p className="mt-1 text-sm text-foreground">
        {rowsRead === 0
          ? "The screen surfaced no names for this edition."
          : `${rowsRead} name${rowsRead === 1 ? "" : "s"} screened, ${funnel.included_count ?? 0} carried, ${funnel.held_count ?? 0} held.`}
      </p>
      {held.length > 0 && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          Held names and the reason each one was passed on:
        </p>
      )}

      {held.length > 0 && (
        <ul className="mt-2 space-y-1">
          {held.map((row, index) => (
            <li
              key={`${row.ticker}-${row.direction ?? ""}-${index}`}
              className="flex flex-wrap items-baseline gap-x-2 text-[12px]"
            >
              <span className="font-medium text-foreground">{row.ticker}</span>
              {row.direction && (
                <span className="text-muted-foreground">{row.direction.toLowerCase()}</span>
              )}
              <span className="text-muted-foreground">
                held — {holdReasonText(row.hold_reason_codes, row.hold_kind)}
              </span>
              {row.evaluated_at && (
                <span className="text-muted-foreground/70">
                  quotes as of {row.evaluated_at}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 text-[11px] text-muted-foreground">
        {funnel.quality_bar != null && <>Quality bar {funnel.quality_bar}. </>}
        {funnel.freshness_window_seconds != null && (
          <>Quotes must be no older than {funnel.freshness_window_seconds}s.</>
        )}
      </p>
    </div>
  );
}

/** Trader-readable phrasing for why a name was held.
 *
 * Falls back to the raw code ONLY when there is no known phrasing for it --
 * an unfamiliar code shown verbatim is honest, whereas a generic "held for
 * other reasons" would hide information the owner may need. */
function holdReasonText(codes: string[], kind: string | null | undefined): string {
  if (codes.length === 0) {
    return kind === "NO_INFORMATION" ? "no live quotes at the time of the run" : "no reason recorded";
  }
  return codes.map(holdReasonPhrase).join("; ");
}

function holdReasonPhrase(code: string): string {
  switch (code) {
    case "CURATED_UNPRICED_PRIMARY_EXPRESSION":
      return "no tradeable structure could be priced";
    case "CURATED_FLOW_CONTRADICTION":
      return "options flow ran against the direction";
    case "HOLD_REASON_UNRECORDED":
      return "no reason recorded";
    default:
      return code;
  }
}

function IdeasSection({
  ideas,
  droppedCount,
  editionAsOf,
  candidateFunnel,
}: {
  ideas: MultiStrategyScreenRow[];
  droppedCount: number;
  /** JS-1082 round 2 (FIX 4): this edition's OWN as_of, threaded down to
      each card so its ticker-view deep link's back-link returns to THIS
      edition, never whatever edition happens to be "today" later. */
  editionAsOf: string | null;
  /** DESK-013 ruling 3. `undefined` for an edition minted before the funnel
      existed -- rendered as an explicit "does not record" note rather than as
      a fabricated empty funnel. */
  candidateFunnel?: EditionCandidateFunnel | null;
}) {
  return (
    <section>
      <h2 className="mb-2 text-lg font-semibold tracking-tight">Today's ideas</h2>
      {ideas.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface/40 px-4 py-6 text-center">
          <p className="text-sm text-muted-foreground">
            {droppedCount > 0
              ? `${droppedCount} idea${droppedCount === 1 ? "" : "s"} could not be displayed.`
              : "No ideas cleared the quality bar for this edition."}
          </p>
          {candidateFunnel ? (
            <CandidateFunnelPanel funnel={candidateFunnel} />
          ) : (
            <p className="mt-2 text-[11px] text-muted-foreground">
              This edition does not record what it screened.
            </p>
          )}
        </div>
      ) : (
        // DESK-013 round 3 (Codex MEDIUM): the funnel renders for EVERY
        // edition that records one, not only empty ones. An edition can
        // carry ideas AND have held candidates; hiding the held names behind
        // a zero-idea condition means the owner never sees what the desk
        // passed on, which is exactly the account ruling 3 requires.
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {ideas.map((row, index) => (
              <IdeaCard key={row.row_id ?? `${row.ticker ?? "idea"}-${index}`} row={row} editionAsOf={editionAsOf} />
            ))}
          </div>
          {droppedCount > 0 && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              {droppedCount} idea{droppedCount === 1 ? "" : "s"} could not be displayed.
            </p>
          )}
          {/* Only when something was actually held -- an edition where every
              candidate was carried has nothing further to explain, and an
              empty panel would be noise. */}
          {candidateFunnel && (candidateFunnel.held_count ?? 0) > 0 && (
            <CandidateFunnelPanel funnel={candidateFunnel} />
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Book strip -- net greeks + coverage + cluster flags, rendered honestly
// (e.g. "greeks 7/10" verbatim from the server, never re-derived).
// ---------------------------------------------------------------------------

function signedText(value: string | null | undefined): string {
  if (value == null) return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  const sign = parsed > 0 ? "+" : "";
  return `${sign}${value}`;
}

function BookStrip({ edition }: { edition: EditionDetail }) {
  const book = edition.book_summary;
  if (!book) {
    return (
      <section className="rounded-lg border border-dashed border-border bg-surface/40 px-4 py-4 text-center">
        <p className="text-sm text-muted-foreground">No book summary published for this edition.</p>
      </section>
    );
  }
  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h2 className="text-lg font-semibold tracking-tight">Book</h2>
      <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span>
          <span className="text-muted-foreground">Net delta</span>{" "}
          <span className="font-mono tabular-nums text-foreground">{signedText(book.net_delta_shares)}</span>
        </span>
        <span>
          <span className="text-muted-foreground">Net vega</span>{" "}
          <span className="font-mono tabular-nums text-foreground">{signedText(book.net_vega_dollars)}</span>
        </span>
        <span>
          <span className="text-muted-foreground">Net theta/day</span>{" "}
          <span className="font-mono tabular-nums text-foreground">{signedText(book.net_theta_dollars_per_day)}</span>
        </span>
        <span>
          <span className="text-muted-foreground">Greeks coverage</span>{" "}
          <span className="font-mono tabular-nums text-foreground">{book.greeks_coverage}</span>
        </span>
      </div>
      {book.cluster_flags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {book.cluster_flags.map((flag) => (
            <Badge key={`${flag.axis}-${flag.key}`} variant="outline" className="border-warning/30 text-warning">
              {flag.label}
            </Badge>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Scoreboard strip -- resolved counts + running record from scoreboard_delta.
// Honest empty state until JS-1061's resolution job exists (see edition.py's
// compute_scoreboard_delta docstring): every ledger entry sits at OPEN today,
// so `running_record_total` is legitimately 0.
// ---------------------------------------------------------------------------

// JS-1096 (wireframe parity): exported so the screener masthead can import
// and reuse this SAME component (NO-TECH-DEBT: one implementation of the
// running-record strip, never a fork) -- see MultiStrategyScreenTable.tsx's
// DeskMasthead, which fetches the SAME today's-edition query this page
// already fetches and renders this exact component with it.
//
// JS-1096 round 2 (Codex review fix -- MED finding 3): the counts and the
// provenance label both now come from `runningRecordFacts` (screenerLabels.ts),
// the SAME primitive DeskMasthead's compact inline block below reads --
// genuine extraction, not two components each re-deriving the same numbers
// from `scoreboard_delta` their own way. This page's own EditionMasthead
// already shows the edition's identity prominently above this card, so the
// label isn't repeated here; the masthead's compact block (which has no
// such header) shows it instead.
export function ScoreboardStrip({ edition }: { edition: EditionDetail }) {
  const facts = runningRecordFacts(edition);
  if (!facts) return null;

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h2 className="text-lg font-semibold tracking-tight">Scoreboard</h2>
      {facts.total === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          No resolved ideas yet — the record starts when the first idea resolves.
        </p>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          {/* Round 2 (adversarial review, MED FIX 6 -- scoreboard links): the
              ledger rows endpoint has no outcome_kind filter (only
              status=OPEN|RESOLVED|FLAGGED), so three separate WON/LOST/
              EXPIRED links all pointing at the SAME status=RESOLVED URL
              read as if they distinguished the outcome kind when they
              cannot -- confusing, not clarifying. Replaced with the plain
              counts (no per-number link) plus ONE clearly-labeled affordance
              for the whole resolved set. An outcome_kind filter on the
              ledger-rows endpoint would let this become three real,
              distinct links -- noted as a possible additive backend
              follow-up, not built here. */}
          <span>
            <span className="font-mono tabular-nums text-gain">{facts.won}</span>{" "}
            <span className="text-muted-foreground">won</span>
          </span>
          <span>
            <span className="font-mono tabular-nums text-loss">{facts.lost}</span>{" "}
            <span className="text-muted-foreground">lost</span>
          </span>
          <span>
            <span className="font-mono tabular-nums text-foreground">{facts.expired}</span>{" "}
            <span className="text-muted-foreground">expired</span>
          </span>
          <span className="text-muted-foreground">
            {facts.resolvedSincePrevious} resolved since previous edition
          </span>
          <Link to="/portal/trade-ideas/ledger?status=RESOLVED" className="underline decoration-dotted underline-offset-2">
            View all resolved outcomes →
          </Link>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Archive rail -- newest-first list of past editions; clicking loads that
// edition. The URL encodes the date so a specific edition is bookmarkable.
// ---------------------------------------------------------------------------

function ArchiveRail({
  api,
  selectedAsOf,
  onSelect,
  forbidden = false,
  enabled = true,
}: {
  api: ApiFn;
  selectedAsOf: string | null;
  onSelect: (asOf: string) => void;
  /** FOLLOW-UP 5: when the main edition query is already known-403'd, the
   * archive endpoint is behind the exact same internal allowlist gate, so
   * it would just 403 too -- render the same internal-access-required copy
   * instead of a second error box. */
  forbidden?: boolean;
  /** FOLLOW-UP 5: gates the archive REQUEST itself, not just which UI is
   * shown. Must stay false while the main query's 403-vs-something-else
   * outcome is still unknown (i.e. while it is pending) -- `forbidden`
   * alone is not enough, since it only becomes true AFTER the main query
   * settles, by which point an ungated archive request would already have
   * fired. The caller passes `!query.isPending && !isForbidden`. */
  enabled?: boolean;
}) {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [history, setHistory] = useState<string[]>([]);
  const archive = useTradeIdeaEditionArchive(api, { limit: 25, beforeAsOf: cursor }, enabled);
  const items = archive.data?.items ?? [];

  if (forbidden) {
    return (
      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="text-lg font-semibold tracking-tight">Archive</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Internal access required — the archive is gated the same as the edition itself.
        </p>
      </section>
    );
  }

  const goOlder = () => {
    const last = items[items.length - 1];
    if (!last?.as_of) return;
    setHistory((prev) => [...prev, cursor ?? ""]);
    setCursor(last.as_of);
  };
  const goNewer = () => {
    setHistory((prev) => {
      const next = [...prev];
      const previousCursor = next.pop();
      setCursor(previousCursor ? previousCursor : undefined);
      return next;
    });
  };

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-tight">Archive</h2>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="outline"
            aria-label="Newer editions"
            title={history.length === 0 ? "No newer edition" : undefined}
            disabled={history.length === 0}
            onClick={goNewer}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            aria-label="Older editions"
            title={items.length === 0 ? "No more entries" : undefined}
            disabled={items.length === 0}
            onClick={goOlder}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      {archive.isPending && <p className="mt-2 text-sm text-muted-foreground">Loading archive…</p>}
      {archive.isError && <p className="mt-2 text-sm text-destructive" role="alert">Could not load the archive.</p>}
      {!archive.isPending && !archive.isError && items.length === 0 && (
        <p className="mt-2 text-sm text-muted-foreground">No past editions yet.</p>
      )}

      {items.length > 0 && (
        <ul className="mt-2 divide-y divide-border">
          {items.map((item: EditionArchiveItem) => {
            const asOf = item.as_of;
            const active = asOf != null && asOf === selectedAsOf;
            return (
              <li key={asOf ?? item.content_hash ?? item.edition_number}>
                <button
                  type="button"
                  disabled={asOf == null}
                  title={asOf == null ? "Date unavailable for this edition" : undefined}
                  onClick={() => asOf && onSelect(asOf)}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 py-2 text-left text-sm hover:text-foreground",
                    active ? "font-semibold text-foreground" : "text-muted-foreground",
                  )}
                  aria-current={active ? "true" : undefined}
                >
                  <span>
                    {item.edition_number != null ? `#${item.edition_number}` : ""} {asOf ?? "—"}
                  </span>
                  <span className="font-mono text-[11px] tabular-nums">{item.idea_count} ideas</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Internal-access-required / no-edition-yet states -- mirrors how the
// screener table degrades: MultiStrategyScreenTable does not special-case a
// 403 today (it renders the shared "Could not load" message), so this page
// adds the honest, distinct copy the task calls for while keeping the same
// "render, don't crash" posture on any other error.
// ---------------------------------------------------------------------------

function InternalAccessRequired() {
  return (
    <div className="rounded-xl border border-dashed border-border bg-surface/40 p-8 text-center">
      <h1 className="text-lg font-semibold text-foreground">Internal access required</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        The daily edition is an internal research surface. Your account isn't on the internal allowlist for it.
      </p>
    </div>
  );
}

function NoEditionYet() {
  return (
    <div className="rounded-xl border border-dashed border-border bg-surface/40 p-8 text-center">
      <h1 className="text-lg font-semibold text-foreground">No edition yet</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        No daily edition has been generated yet. Check back after today's edition is published.
      </p>
    </div>
  );
}

function EditionLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-5">
      <h1 className="font-semibold">We couldn't load this edition</h1>
      <p className="mt-1 text-sm text-muted-foreground">The edition is temporarily unavailable. Try again to reload it.</p>
      <Button className="mt-3" size="sm" variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export interface EditionPageProps {
  api: ApiFn;
  /** ISO date (YYYY-MM-DD) from the URL, or null/undefined for "today". */
  asOf?: string | null;
  /** Called when the reader picks a different edition from the archive rail
   * (or navigates back to "today"); the caller owns the URL. */
  onSelectAsOf?: (asOf: string) => void;
}

export function EditionPage({ api, asOf, onSelectAsOf }: EditionPageProps) {
  const hasExplicitDate = Boolean(asOf);
  const byDate = useTradeIdeaEditionByAsOf(api, asOf ?? null, hasExplicitDate);
  const today = useTradeIdeaEditionToday(api, !hasExplicitDate);
  const query = hasExplicitDate ? byDate : today;

  const error = query.error;
  const status = isApiError(error) ? error.status : null;
  const isForbidden = status === 403;
  const isNotFound = status === 404;

  const edition = query.data;
  const selectedAsOf = edition?.as_of ?? asOf ?? null;

  const { eligible, shadow } = useMemo(() => {
    const ideas = edition?.ideas ?? [];
    const decorated = ideas.map((row) => ({ row, shadow: isShadowEntry(topEntry(row)) }));
    return {
      eligible: decorated.filter((item) => !item.shadow).map((item) => item.row),
      shadow: decorated.filter((item) => item.shadow).map((item) => item.row),
    };
  }, [edition]);

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex items-center gap-2">
        <Badge variant="secondary">Trade Ideas</Badge>
        <Badge variant="outline">Daily edition</Badge>
      </div>

      {query.isPending && (
        <div className="space-y-3" role="status" aria-label="Loading edition">
          <div className="h-8 w-64 animate-pulse rounded-md bg-hover" />
          <div className="h-4 w-96 animate-pulse rounded-md bg-hover" />
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="h-24 animate-pulse rounded-md bg-hover" />
          ))}
        </div>
      )}

      {!query.isPending && isForbidden && <InternalAccessRequired />}
      {!query.isPending && !isForbidden && isNotFound && <NoEditionYet />}
      {!query.isPending && !isForbidden && !isNotFound && query.isError && (
        <EditionLoadError onRetry={() => query.refetch()} />
      )}

      {!query.isPending && !query.isError && edition && (
        <Fragment>
          <EditionMasthead edition={edition} api={api} />
          <RegimeLine edition={edition} />
          {/* JS-1149 (DESK-012): which desk rules judged this edition's ideas.
              Read off the ideas themselves, so this needs no second fetch --
              and across ALL of them, since an edition is built from the same
              curated population and can likewise span a rules activation. */}
          <DeskRulesStrip rows={edition.ideas} />
          <IdeasSection
            ideas={[...eligible, ...shadow]}
            droppedCount={edition.ideas_dropped_count ?? 0}
            editionAsOf={edition.as_of ?? null}
            candidateFunnel={edition.candidate_funnel}
          />
          <BookStrip edition={edition} />
          <ScoreboardStrip edition={edition} />
        </Fragment>
      )}

      <ArchiveRail
        api={api}
        selectedAsOf={selectedAsOf}
        onSelect={(nextAsOf) => onSelectAsOf?.(nextAsOf)}
        forbidden={isForbidden}
        enabled={!query.isPending && !isForbidden}
      />

      <footer className="text-xs leading-relaxed text-muted-foreground">
        Internal research only. Not customer-authoritative. Prices are quoted markets, not guaranteed fills.
      </footer>
    </div>
  );
}
